import { memo, useRef, useState, ReactNode } from 'react';
import { motion, useSpring, useMotionValue, useTransform } from 'framer-motion';
import { useUIV2Safe } from '@/contexts/UIV2Context';
import { useReduceMotion } from '@/hooks/useReduceMotion';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface HoloCardProps {
  children: ReactNode;
  className?: string;
  tiltStrength?: number;
  glowOnHover?: boolean;
  holoEffect?: boolean;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  role?: string;
  tabIndex?: number;
  'aria-label'?: string;
  'data-tour'?: string;
}

/**
 * HoloCard - Glassmorphic card with holographic effects
 * Features:
 * - 3D tilt on mouse move (desktop only)
 * - Holographic gradient border
 * - Glow effect on hover
 * - Respects LOW_FX and reduce motion
 */
export const HoloCard = memo(function HoloCard({
  children,
  className = '',
  tiltStrength = 8,
  glowOnHover = true,
  holoEffect = true,
  onClick,
  onKeyDown,
  role,
  tabIndex,
  'aria-label': ariaLabel,
  'data-tour': dataTour,
}: HoloCardProps) {
  const { isLowFx, uiV2Enabled } = useUIV2Safe();
  const reduceMotion = useReduceMotion();
  const isMobile = useIsMobile();
  
  const ref = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  
  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);
  
  const springConfig = { damping: 25, stiffness: 300 };
  
  const rotateX = useSpring(
    useTransform(mouseY, [0, 1], [tiltStrength, -tiltStrength]),
    springConfig
  );
  const rotateY = useSpring(
    useTransform(mouseX, [0, 1], [-tiltStrength, tiltStrength]),
    springConfig
  );
  
  // Light position for holographic effect
  const lightX = useTransform(mouseX, [0, 1], ['0%', '100%']);
  const lightY = useTransform(mouseY, [0, 1], ['0%', '100%']);
  
  const isInteractive = uiV2Enabled && !isLowFx && !reduceMotion && !isMobile;
  
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current || !isInteractive) return;
    
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    mouseX.set(x);
    mouseY.set(y);
  };
  
  const handleMouseLeave = () => {
    mouseX.set(0.5);
    mouseY.set(0.5);
    setIsHovered(false);
  };
  
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  
  // Fallback to simpler card if V2 disabled
  if (!uiV2Enabled) {
    return (
      <div
        className={cn(
          'bg-card border border-border rounded-xl p-6',
          onClick && 'cursor-pointer',
          className
        )}
        onClick={onClick}
        onKeyDown={onKeyDown}
        role={role}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
        data-tour={dataTour}
      >
        {children}
      </div>
    );
  }
  
  return (
    <motion.div
      ref={ref}
      className={cn(
        'relative overflow-hidden rounded-xl',
        'v2-glass',
        'border border-border/50',
        'transition-shadow duration-300',
        isHovered && glowOnHover && !isLowFx && 'v2-glow',
        onClick && 'cursor-pointer',
        className
      )}
      style={isInteractive ? {
        rotateX,
        rotateY,
        transformStyle: 'preserve-3d',
        perspective: 1000,
      } : undefined}
      onMouseMove={isInteractive ? handleMouseMove : undefined}
      onMouseLeave={isInteractive ? handleMouseLeave : undefined}
      onMouseEnter={isInteractive ? handleMouseEnter : undefined}
      whileHover={isInteractive ? { scale: 1.01, z: 10 } : undefined}
      whileTap={onClick ? { scale: 0.99 } : undefined}
      transition={{ duration: 0.2 }}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={role}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      data-tour={dataTour}
    >
      {/* Holographic gradient overlay */}
      {holoEffect && !isLowFx && (
        <motion.div
          className="absolute inset-0 pointer-events-none opacity-0"
          style={{
            background: `radial-gradient(circle at ${lightX} ${lightY}, 
              hsl(var(--primary) / 0.15), 
              hsl(var(--accent) / 0.1) 30%,
              transparent 60%
            )`,
          }}
          animate={{
            opacity: isHovered ? 1 : 0,
          }}
          transition={{ duration: 0.3 }}
        />
      )}
      
      {/* Animated border glow */}
      {isInteractive && isHovered && (
        <motion.div
          className="absolute inset-0 pointer-events-none rounded-xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div 
            className="absolute inset-0 rounded-xl v2-holo-border"
            style={{
              padding: '1px',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
            }}
          />
        </motion.div>
      )}
      
      {/* Content */}
      <div 
        className="relative z-10"
        style={isInteractive ? { transform: 'translateZ(20px)' } : undefined}
      >
        {children}
      </div>
    </motion.div>
  );
});
